import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavParams, ToastController, ViewController } from "ionic-angular";
import {
  AsyncReferralSummary,
  SummaryItem,
} from "models/patient-chart/summary-types";
import { BehaviorSubject, Subject } from "rxjs";
import { interval } from "rxjs/observable/interval";
import { merge } from "rxjs/observable/merge";
import { of } from "rxjs/observable/of";
import {
  catchError,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from "rxjs/operators";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
@Component({
  selector: "generate-ai-summary",
  templateUrl: "generate-ai-summary-modal.html",
})
export class GenerateAISummary implements OnInit, OnDestroy {
  documentsIds: number[];
  patientId: number;
  existingSummaryPresent: boolean;
  summaryLoading: boolean = false;
  referralSummaryId: number;
  acceptButtonLoading: boolean = false;
  generateFileError: string = "";
  public patientReferralSummaryData = null;
  createIntakeMeds: boolean = false;

  elapsedTime: number = 0;
  status: string = "Not started";
  isPolling: boolean = false;
  isSyncing: boolean = false;

  private startTime: number = 0;
  private destroy$ = new Subject<void>();
  private forceRefresh$ = new BehaviorSubject<boolean>(false);
  private readonly MAX_WAIT_TIME = 10 * 60 * 1000; // 10 minutes in milliseconds
  constructor(
    public viewCtrl: ViewController,
    public navParams: NavParams,
    private referralsService: ReferralsServiceProvider,
    private toastCtrl: ToastController
  ) {
    this.documentsIds = navParams.get("documentsIds");
    this.patientId = navParams.get("patientId");
    const existingSummary = this.navParams.get("existingSummary") || null;

    // If existing summary has items array, merge them
    if (existingSummary?.items) {
      this.patientReferralSummaryData = this.mergeSummaryItems(
        existingSummary.items
      );
    } else {
      this.patientReferralSummaryData = existingSummary;
    }

    this.existingSummaryPresent = !!this.patientReferralSummaryData;
  }
  ngOnInit() {
    // If no existing summary is provided, load from API when documentsIds are available
    if (
      !this.patientReferralSummaryData &&
      this.documentsIds &&
      this.documentsIds.length > 0
    ) {
      this.loadReferralSummary();
    }
  }

  loadReferralSummary() {
    this.summaryLoading = true;
    this.generateFileError = ""; // clear previous errors
    this.referralsService
      .initiateReferralSummary(this.patientId, this.documentsIds)
      .subscribe(
        (response) => {
          // Update this.data with the API response
          if (response) {
            // this.patientReferralSummaryData = response;
            // this.summaryLoading = false;
            this.referralSummaryId = response.id;
            this.startPollingForSummary();
          }
        },
        (error) => {
          console.error("Error loading referral summary:", error);
          this.summaryLoading = false;
          this.generateFileError =
            "Something went wrong, please try again later.";
        }
      );
  }
  closeModal() {
    this.viewCtrl.dismiss({ cancel: true });
  }

  onAccept() {
    if (this.createIntakeMeds) {
      this.createReferralSummaryWithIntakeMeds();
    } else {
      this.createReferralSummary();
    }
  }

  createReferralSummary() {
    if (this.patientReferralSummaryData && this.patientId) {
      this.acceptButtonLoading = true;
      this.referralsService
        .promptReferralSummary(this.patientId, this.referralSummaryId, true)
        .subscribe(
          (response) => {
            this.acceptButtonLoading = false;
            // Close modal on success
            this.viewCtrl.dismiss({ accepted: true, data: response });
          },
          (error) => {
            console.error("Error creating referral summary:", error);
            this.acceptButtonLoading = false;
            this.showErrorAlert(
              "Failed to accept the referral summary. Please try again."
            );
          }
        );
    }
  }

  createReferralSummaryWithIntakeMeds() {
    if (this.patientReferralSummaryData && this.patientId) {
      this.acceptButtonLoading = true;

      // Extract medications from the summary data
      const medications = this.extractMedicationsFromSummary();

      // First create the referral summary
      this.referralsService
        .promptReferralSummary(this.patientId, this.referralSummaryId, true)
        .subscribe(
          (response) => {
            // Then create intake medications
            this.referralsService
              .createReferralSummaryWithIntakeMeds(medications, this.patientId)
              .subscribe(
                (medResponse) => {
                  this.acceptButtonLoading = false;
                  // Close modal on success
                  this.viewCtrl.dismiss({
                    accepted: true,
                    data: response,
                    intakeMedsCreated: true,
                    intakeMedsData: medResponse,
                  });
                },
                (error) => {
                  console.error("Error creating intake medications:", error);
                  this.acceptButtonLoading = false;
                  this.showErrorAlert(
                    "Referral summary created but failed to create intake medications. Please try again."
                  );
                }
              );
          },
          (error) => {
            console.error("Error creating referral summary:", error);
            this.acceptButtonLoading = false;
            this.showErrorAlert(
              "Failed to accept the referral summary. Please try again."
            );
          }
        );
    }
  }

  private mergeSummaryItems(items: SummaryItem[]): SummaryItem | null {
    if (!items || items.length === 0) {
      return null;
    }

    // Initialize merged object with first item's structure
    const merged: SummaryItem = {
      Patient: {
        DOB: "none",
        FullAddress: "none",
        Gender: "none",
        Name: "none",
      },
      ProvidersAndContacts: [],
      FacilitiesOrLocations: [],
      Medical: {
        Allergies: [],
        Diagnoses: {
          PrimaryDiagnosis: "none",
          SecondaryDiagnoses: [],
          OtherComorbidities: [],
        },
        Medications: [],
        Directives: [],
      },
      PhysicalNotesSummary: "none",
      LabResultsNotes: "none",
    };

    // Merge all items
    items.forEach((item) => {
      // Merge Patient info
      if (item.Patient) {
        if (
          item.Patient.Name &&
          item.Patient.Name !== "none" &&
          merged.Patient.Name === "none"
        ) {
          merged.Patient.Name = item.Patient.Name;
        }
        if (
          item.Patient.DOB &&
          item.Patient.DOB !== "none" &&
          merged.Patient.DOB === "none"
        ) {
          merged.Patient.DOB = item.Patient.DOB;
        }
        if (
          item.Patient.Gender &&
          item.Patient.Gender !== "none" &&
          merged.Patient.Gender === "none"
        ) {
          merged.Patient.Gender = item.Patient.Gender;
        }
        if (
          item.Patient.FullAddress &&
          item.Patient.FullAddress !== "none" &&
          merged.Patient.FullAddress === "none"
        ) {
          merged.Patient.FullAddress = item.Patient.FullAddress;
        }
      }

      // Merge Providers and Contacts
      if (item.ProvidersAndContacts?.length) {
        merged.ProvidersAndContacts.push(...item.ProvidersAndContacts);
      }

      // Merge Facilities/Locations
      if (item.FacilitiesOrLocations?.length) {
        merged.FacilitiesOrLocations.push(...item.FacilitiesOrLocations);
      }

      // Merge Medical data
      if (item?.Medical) {
        // Merge Allergies (combine all unique)
        if (item.Medical.Allergies?.length) {
          merged.Medical.Allergies.push(...item.Medical.Allergies);
        }

        // Merge Diagnoses
        if (item.Medical?.Diagnoses) {
          if (
            item.Medical.Diagnoses.PrimaryDiagnosis &&
            item.Medical.Diagnoses.PrimaryDiagnosis !== "none" &&
            merged.Medical.Diagnoses.PrimaryDiagnosis === "none"
          ) {
            merged.Medical.Diagnoses.PrimaryDiagnosis =
              item.Medical.Diagnoses.PrimaryDiagnosis;
          }

          if (item.Medical.Diagnoses?.SecondaryDiagnoses?.length) {
            item.Medical.Diagnoses.SecondaryDiagnoses.forEach((diag) => {
              if (!merged.Medical.Diagnoses.SecondaryDiagnoses.includes(diag)) {
                merged.Medical.Diagnoses.SecondaryDiagnoses.push(diag);
              }
            });
          }

          if (item.Medical.Diagnoses?.OtherComorbidities?.length) {
            item.Medical.Diagnoses.OtherComorbidities.forEach((comorb) => {
              if (
                !merged.Medical.Diagnoses.OtherComorbidities.includes(comorb)
              ) {
                merged.Medical.Diagnoses.OtherComorbidities.push(comorb);
              }
            });
          }
        }

        // Merge Medications (combine all unique)
        if (item.Medical.Medications?.length) {
          item.Medical.Medications.forEach((med) => {
            if (
              !merged.Medical.Medications.some(
                (m) =>
                  m.Medication === med.Medication &&
                  m.Strength === med.Strength &&
                  m.Dose === med.Dose
              )
            ) {
              merged.Medical.Medications.push({ ...med });
            }
          });
        }

        // Merge Directives (combine all unique)
        if (item.Medical.Directives?.length) {
          item.Medical.Directives.forEach((directive) => {
            if (!merged.Medical.Directives.includes(directive)) {
              merged.Medical.Directives.push(directive);
            }
          });
        }
      }

      // Merge Physical Notes (concatenate if multiple)
      if (item?.PhysicalNotesSummary && item.PhysicalNotesSummary !== "none") {
        if (merged.PhysicalNotesSummary === "none") {
          merged.PhysicalNotesSummary = item.PhysicalNotesSummary;
        } else {
          merged.PhysicalNotesSummary += "\n\n" + item.PhysicalNotesSummary;
        }
      }

      // Merge Lab Results (concatenate if multiple)
      if (item?.LabResultsNotes && item.LabResultsNotes !== "none") {
        if (merged.LabResultsNotes === "none") {
          merged.LabResultsNotes = item.LabResultsNotes;
        } else {
          merged.LabResultsNotes += "\n\n" + item.LabResultsNotes;
        }
      }
    });

    return merged;
  }

  private extractMedicationsFromSummary() {
    const medications = [];

    if (this.patientReferralSummaryData?.Medical?.Medications) {
      this.patientReferralSummaryData.Medical.Medications.forEach((med) => {
        medications.push({
          name: med?.Medication || "",
          dosage: med?.Dose || null,
          frequency: med?.Frequency || null,
          route: med?.route || null,
          strength: med?.Strength || null,
          isFreeText: true,
        });
      });
    }

    return medications;
  }

  onReject() {
    this.closeModal();
  }

  syncSummaryStatus() {
    this.forceRefresh$.next(true);
  }

  shoudlContinuePolling() {
    const elapsed = Date.now() - this.startTime;
    const elapsedTime = elapsed < this.MAX_WAIT_TIME && this.isPolling;

    if (!elapsedTime) {
      this.generateFileError = "Maximum wait time reached.Please try again";
      this.summaryLoading = false;
    }
    return elapsedTime;
  }

  stopPolling() {
    this.isPolling = false;
    this.destroy$.next();
  }

  startPollingForSummary() {
    this.summaryLoading = true;
    this.isPolling = true;
    this.startTime = Date.now();

    const automaticPoll$ = interval(10000);

    merge(automaticPoll$, this.forceRefresh$)
      .pipe(
        takeWhile(() => this.shoudlContinuePolling()),
        takeUntil(this.destroy$),
        tap(() => {
          this.isSyncing = true;
          this.elapsedTime = Date.now() - this.startTime;
        }),
        switchMap(() =>
          this.referralsService
            .getAsyncReferralSummary(this.patientId, this.referralSummaryId)
            // CO-3358 — catchError used to sit on the OUTER pipe, below the
            // switchMap. There it REPLACED the source, so the very first
            // transient poll failure completed the whole stream and polling
            // never resumed — the modal sat on its spinner until the 10-minute
            // cap and then reported "Maximum wait time reached". Catching
            // per-request keeps the interval alive across a blip.
            .pipe(
              catchError(() => {
                this.isSyncing = false;
                return of(null);
              })
            )
        )
      )
      .subscribe({
        next: (response: AsyncReferralSummary) => {
          this.isSyncing = false;
          this.elapsedTime = Date.now() - this.startTime;

          // CO-3358 — a caught poll failure emits null here, and this used to
          // read `response.status` unguarded, throwing inside the subscriber.
          if (!response) {
            return;
          }

          if (response.status === "READY") {
            this.summaryLoading = false;

            // TODO: Remove this dummy data mapping once backend is ready
            // For now, use dummy data if response doesn't have items
            let summaryData = response;

            if (summaryData?.summaryText?.items) {
              this.patientReferralSummaryData = this.mergeSummaryItems(
                summaryData.summaryText.items
              );
            } else {
              this.patientReferralSummaryData = summaryData?.summaryText;
            }

            this.stopPolling();
          } else if (!this.shoudlContinuePolling()) {
            this.stopPolling();
          }
        },
        complete: () => {
          this.isPolling = false;
        },
      });
  }

  showErrorAlert(message: string) {
    const toast = this.toastCtrl.create({
      message: message,
      duration: 4000,
      position: "top",
      cssClass: "error-toast",
      showCloseButton: true,
      closeButtonText: "OK",
    });
    toast.present();
  }

  ngOnDestroy(): void {
    this.forceRefresh$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}



// WEBPACK FOOTER //
// ./src/components/intake-queue/generate-ai-summary-modal/generate-ai-summary-modal.ts